/**
 * inPersonPay.ts: the $1 in-person fee's payment element on the website (card
 * 1218526698505993 for Stripe.js; the fee itself is card 1218886635703438).
 *
 * Blake's screens (inPerson.ts, #972) left one seam for the payment step,
 * `window.__gbzInPersonPay`, and a payer's button stays disabled until
 * something fills it. This fills it. Nothing here talks to our server: every
 * call to start, authorize-fee and confirm stays in inPerson.ts, and this file
 * only turns a clientSecret the server handed out into an authorized hold.
 *
 * THE CONTRACT IT SERVES (artifacts/api-server/src/routes/offersInPerson.ts):
 * one PaymentIntent per payer, amount IN_PERSON_FEE_CENTS (100), currency usd,
 * payment_method_types ["card"], capture_method "manual". It starts at
 * requires_payment_method and only the payer's own device can move it to
 * requires_capture, by confirming it with Stripe.js. That is this file.
 *
 *   mount(host, opts)  loads Stripe.js lazily (only a payer's confirm screen
 *                      ever loads it) and mounts a Payment Element in DEFERRED
 *                      mode, because the first payer's intent does not exist
 *                      until their tap: `start` both records their confirm and
 *                      creates it. The Elements options mirror the server's
 *                      intent exactly (FEE_ELEMENTS_OPTIONS); Stripe refuses a
 *                      confirm whose options disagree with the intent.
 *                      onReady fires when the element is really there, and
 *                      that is what enables Blake's button.
 *   validate()         the card is complete, BEFORE the first payer's start
 *                      records their confirm (inPerson.ts attemptConfirm).
 *   confirmCard()      elements.submit(), then opts.intent() for the secret the
 *                      server already handed out, then stripe.confirmPayment
 *                      with redirect "if_required" (a 3DS challenge runs in
 *                      Stripe's own frame; a card never redirects). A decline
 *                      throws Stripe's own sentence, which is E3.
 *   reset()            "Try another card": clear the element.
 *
 * 🔴 NO STATUS IS READ, WITH ONE EXCEPTION. confirmPayment resolving without an
 * error is the signal taken from the browser, and even that only moves the
 * screen along: the server's confirm route retrieves every payer's intent and
 * refuses unless each is requires_capture or succeeded. The browser's word is
 * never what completes a deal (spec §4 S5, lying state 3).
 *   The exception is check() (IP4b, M8): after an unknown outcome it reads the
 * intent back, so Try again never re-confirms a hold that went through (the
 * audit's probe P13, where the payer looped on Stripe's refusal). It only
 * decides whether to confirm the card again; the server's confirm still
 * verifies every hold. inPersonPay.test.ts holds the read to check() alone.
 *
 * CARD ONLY, FOR NOW. Wallets are off in the element and onReady always says
 * { wallet: false }, so Blake's wallet mode never engages. Apple Pay and
 * Google Pay need the Express Checkout Element plus each domain registered
 * with Stripe, and Blake's wallet layout has no re-confirm button and does not
 * render a refused confirm's sentence, so a wallet payer whose confirm was
 * refused would see nothing. That is a follow-up, not a Wednesday blocker.
 * Link is off too: the only allowed type is card.
 *
 * WITHOUT A KEY THIS INSTALLS NOTHING, so Blake's honest state stands: the
 * payer's button is disabled and says card payment isn't connected, and a tap
 * posts nothing.
 */

import { PAY_FORM_FAILED, PAY_UNCONFIRMED, type PayHook } from "./inPerson";
import {
  getPublishableKey,
  loadStripeJs,
  type StripeElementLike,
  type StripeElementsLike,
  type StripeLike,
  type StripeErrorLike,
} from "./stripeJs";
import { publishableKeyOrNull } from "../lib/stripePublishableKey";

/**
 * The Elements group for one fee, mirroring the server's PaymentIntent
 * (offersInPerson.ts authorizeFee: currency "usd", payment_method_types
 * ["card"], capture_method "manual"). The amount is the fee the screen was
 * given, which is the server's feeCents. inPersonPay.test.ts parses the server
 * file and holds these equal.
 */
export const FEE_ELEMENTS_OPTIONS = Object.freeze({
  mode: "payment",
  currency: "usd",
  captureMethod: "manual",
  paymentMethodTypes: Object.freeze(["card"]),
});

/** Card only: no Apple Pay or Google Pay inside the element (see the header). */
export const PAYMENT_ELEMENT_OPTIONS = Object.freeze({
  wallets: Object.freeze({ applePay: "never", googlePay: "never" }),
});

interface Mounted {
  host: HTMLElement;
  opts: Parameters<PayHook["mount"]>[1];
  setup: Promise<{ stripe: StripeLike; elements: StripeElementsLike; element: StripeElementLike } | null>;
  ready: boolean;
  dead: boolean;
}

/** Stripe's own sentence for an error. Empty lets inPerson.ts fall back to its own. */
function sentence(error: StripeErrorLike | undefined): string {
  return typeof error?.message === "string" ? error.message : "";
}

/** The element in the screen's own colors, day or night. Only hex values are passed on. */
function appearanceFor(host: HTMLElement, win: Window): Record<string, unknown> {
  /* Walked by hand rather than with closest(): the host is not a control, and
     guardedWritesFailSafe.test.ts reads every closest() in this layer as one. */
  let night = win.document.getElementById("app")?.getAttribute("data-mode") === "night";
  for (let el: Element | null = host; el && !night; el = el.parentElement) {
    if (el.getAttribute("data-mode") === "night") night = true;
  }
  const variables: Record<string, string> = { borderRadius: "12px" };
  let style: CSSStyleDeclaration | null = null;
  try { style = win.getComputedStyle(host); } catch { style = null; }
  const tokens: Array<[string, string]> = [
    ["colorPrimary", night ? "--gold" : "--accent"],
    ["colorText", "--ink"],
    ["colorTextSecondary", "--ink-muted"],
    ["colorBackground", "--card"],
    ["colorDanger", "--destructive-text"],
  ];
  for (const [name, token] of tokens) {
    const value = style?.getPropertyValue(token).trim() ?? "";
    if (/^#[0-9a-fA-F]{3,8}$/.test(value)) variables[name] = value;
  }
  return { theme: night ? "night" : "stripe", variables };
}

export function createInPersonPayHook(
  win: Window,
  { load, key }: { load: (src: string) => Promise<void>; key: string },
): Required<PayHook> {
  let current: Mounted | null = null;

  /** The mounted, ready element, or the card form's failure line. With this hook installed the build has a key, so
   *  "isn't connected" is never true here (IP4a H5). */
  async function live(): Promise<{ m: Mounted; stripe: StripeLike; elements: StripeElementsLike; element: StripeElementLike }> {
    const m = current;
    if (!m) throw new Error(PAY_FORM_FAILED);
    const parts = await m.setup;
    if (!parts || !m.ready || m.dead) throw new Error(PAY_FORM_FAILED);
    return { m, ...parts };
  }

  async function submitOrThrow(elements: StripeElementsLike): Promise<void> {
    const { error } = await elements.submit();
    if (error) throw new Error(sentence(error));
  }

  return {
    mount(host, opts) {
      const target = win.document.createElement("div");
      target.className = "ip-pay-element";
      host.appendChild(target);
      const m: Mounted = { host, opts, setup: Promise.resolve(null), ready: false, dead: false };
      current = m;
      m.setup = (async () => {
        const stripe = await loadStripeJs({ load, key, win });
        if (m.dead) return null;
        /* Stripe.js did not load (stripeJs.ts forgets the attempt, so a remount loads it again). */
        if (!stripe) { opts.onLoadFailed?.(); return null; }
        const amount = opts.feeCents;
        if (!Number.isInteger(amount) || amount <= 0) {
          console.warn("[in-person-pay] no fee to take on this screen (feeCents " + String(amount) + "); the element is not mounted");
          return null;
        }
        const elements = stripe.elements({
          ...FEE_ELEMENTS_OPTIONS,
          paymentMethodTypes: [...FEE_ELEMENTS_OPTIONS.paymentMethodTypes],
          amount,
          appearance: appearanceFor(host, win),
        });
        const element = elements.create("payment", {
          wallets: { ...PAYMENT_ELEMENT_OPTIONS.wallets },
        });
        element.on("ready", () => {
          if (m.dead || m.ready) return;
          m.ready = true;
          opts.onReady({ wallet: false });
        });
        element.on("loaderror", (event) => {
          console.warn("[in-person-pay] the payment element failed to load; card payment stays off:", event);
          if (!m.dead && !m.ready) opts.onLoadFailed?.();
        });
        element.mount(target);
        return { stripe, elements, element };
      })().catch((err: unknown) => {
        console.warn("[in-person-pay] the payment element could not be set up; card payment stays off:", err);
        if (!m.dead) opts.onLoadFailed?.();
        return null;
      });
      return () => {
        m.dead = true;
        if (current === m) current = null;
        void m.setup.then((parts) => {
          try { parts?.element.destroy(); } catch { /* already gone with its frame */ }
        });
        target.remove();
      };
    },

    async validate() {
      const { elements } = await live();
      await submitOrThrow(elements);
    },

    async confirmCard() {
      const { m, stripe, elements } = await live();
      await submitOrThrow(elements);
      const clientSecret = await m.opts.intent();
      if (m.dead) throw new Error(PAY_FORM_FAILED);
      const { error } = await stripe.confirmPayment({
        elements,
        clientSecret,
        confirmParams: { return_url: win.location.href },
        redirect: "if_required",
      });
      /* 🔴 api_connection_error ONLY, and the narrowness is the point. That type
         means the request to Stripe never completed, so the $1 may be
         authorized and this payer must not be told it failed. Every other type —
         a decline, a validation error — leaves nothing held and keeps Stripe's
         own sentence, which is more specific than anything we would write.
         📌 Deliberately NOT applied to submitOrThrow above: that is the card
         CHECK, before any intent is confirmed, where nothing can be held. */
      if (error) {
        throw new Error(error.type === "api_connection_error" ? PAY_UNCONFIRMED : sentence(error));
      }
    },

    reset() {
      const m = current;
      if (!m) return;
      void m.setup.then((parts) => {
        try { parts?.element.clear?.(); } catch { /* nothing typed to clear */ }
      });
    },

    /* IP4b (M8): after an unknown outcome (PAY_UNCONFIRMED), the intent read back. "held" is requires_capture (the
       hold is in) or succeeded; anything else is "unpaid", and the card is confirmed again. A read that fails is
       still unknown, so it throws. A Stripe without the method (a test double) keeps today's re-confirm. */
    async check(clientSecret) {
      const parts = current ? await current.setup : null;
      if (!parts) throw new Error(PAY_UNCONFIRMED);
      if (typeof parts.stripe.retrievePaymentIntent !== "function") return "unpaid";
      const { paymentIntent, error } = await parts.stripe.retrievePaymentIntent(clientSecret);
      if (error || !paymentIntent) throw new Error(PAY_UNCONFIRMED);
      return paymentIntent.status === "requires_capture" || paymentIntent.status === "succeeded" ? "held" : "unpaid";
    },
  };
}

/**
 * Fill Blake's seam, or leave it empty. Returns whether the hook is installed.
 * Without a publishable key it installs nothing and says so in the console;
 * the screen already says card payment isn't connected.
 */
export function installInPersonPay(
  win: Window,
  { load, key = getPublishableKey() }: { load: (src: string) => Promise<void>; key?: string | null },
): boolean {
  const pk = publishableKeyOrNull(key);
  if (!pk) {
    console.warn("[in-person-pay] no Stripe publishable key in this build: the $1 in-person fee stays disabled");
    return false;
  }
  (win as Window & { __gbzInPersonPay?: PayHook }).__gbzInPersonPay = createInPersonPayHook(win, { load, key: pk });
  return true;
}
